import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import '../styles/FAQPage.css';

const FAQPage = ({ translations, language }) => {
  const t = translations[language];
  const [openIndex, setOpenIndex] = useState(null);

  // Helper function to parse URLs in text and make them clickable
  const parseAnswerWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a key={index} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const faqs = [
    {
      question: t.faq1Q || 'How often are prices updated?',
      answer: t.faq1A || 'Prices are updated daily from all major Canadian retailers and carriers.'
    },
    {
      question: t.faq2Q || 'Are the prices accurate?',
      answer: t.faq2A || 'We scrape data directly from retailer websites. While we strive for accuracy, please verify final pricing at the store before purchase.'
    },
    {
      question: t.faq3Q || 'What does "Bring It Back" mean?',
      answer: t.faq3A || 'Bring It Back is a program where you return your device after the contract period for a reduced total cost. The premium shown is what you pay over the contract term.'
    },
    {
      question: t.faq4Q || 'Why do some stores show N/A?',
      answer: t.faq4A || 'N/A means the phone is currently unavailable or out of stock at that retailer for the selected carrier and storage combination.'
    },
    {
      question: t.faq5Q || 'How do gift cards work?',
      answer: t.faq5A || 'Gift cards are promotional credits offered by retailers. The "Price After GC" reflects the effective price after applying the gift card value.'
    },
    {
      question: t.faq6Q || 'Can I filter by carrier?',
      answer: t.faq6A || 'Yes! Use the filters panel on the left (or tap the filters button on mobile) to select specific carriers, stores, storage sizes, and more.'
    },
    {
      question: t.faq7Q || 'What carriers do you track?',
      answer: t.faq7A || 'We track all major Canadian carriers: Bell, Rogers, Telus, Virgin Plus, Fido, Koodo, and Freedom Mobile.'
    },
    {
      question: t.faq8Q || 'Is MobiDeal free?',
      answer: t.faq8A || 'Yes, MobiDeal is completely free and always will be. This is a personal project, not a business venture. While we have ad spaces on the site, they are currently inactive. This project exists to help Canadians find the best phone deals, not to make money.'
    },
    {
      question: t.faq9Q || 'How does the Plans comparison feature work?',
      answer: t.faq9A || 'The Plans page allows you to compare phone plans from all major carriers side-by-side, filtering by data, price, and plan type (BYOD vs With Device). You can easily see which carrier offers the best value for your needs. Note that plan pricing is currently in beta and should be verified directly with carriers.'
    },
    {
      question: t.faq10Q || 'How does the Calculator feature work?',
      answer: t.faq10A || 'The Calculator helps you understand the true cost of a phone plan by accounting for device financing, plan differences between BYOD and device-eligible plans, gift cards, promotions, and other factors. It shows you whether you\'re getting a good deal compared to buying a device outright with a BYOD plan.'
    },
    {
      question: t.faq11Q || 'I have a feature request',
      answer: t.faq11A || 'You\'re welcome to give me suggestions! Please forward them to admin@mobideal.ca'
    },
    {
      question: t.faq12Q || 'I love your project! Let\'s connect on LinkedIn',
      answer: t.faq12A || 'I would love connecting with you all! I\'m a new grad computer science graduate trying to connect with people in the industry. Feel free to add me at https://www.linkedin.com/in/simo-hakim/'
    },
    {
      question: t.faq13Q || 'My favorite carrier is missing',
      answer: t.faq13A || 'We\'re expanding the carriers displayed soon, but for now we only focus on those offering phones through the major retailers (Best Buy, Costco, Walmart).'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="faq-container">
        <h1 className="faq-title">{t.faqTitle || 'Frequently Asked Questions'}</h1>
        <p className="faq-subtitle">
          {t.faqSubtitle || 'Find answers to common questions about MobiDeal'}
        </p>

        <div className="faq-list">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
              <button className="faq-question" onClick={() => toggleFAQ(index)}>
                <span>{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="faq-icon" />
                ) : (
                  <ChevronDown className="faq-icon" />
                )}
              </button>
              {openIndex === index && (
                <div className="faq-answer">
                  <p>{parseAnswerWithLinks(faq.answer)}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="faq-contact">
          <h2>{t.stillHaveQuestions || 'Still have questions?'}</h2>
          <p>
            {t.contactUs || 'Contact us at'} <a href="mailto:admin@mobideal.ca">admin@mobideal.ca</a>
          </p>
        </div>

        <div className="faq-trademarks">
          <p>
            {t.trademarks || 'Apple, Google, Samsung, Motorola, Best Buy, Costco, Walmart, Bell, Virgin Plus, Koodo, Telus, Rogers, Fido, and Freedom Mobile are registered trademarks of their respective owners. MobiDeal is not affiliated with or endorsed by any of these companies.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
